import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  FormControl,
  Input,
  InputGroup,
  InputRightElement,
  Box,
  Flex,
  Text,
} from "@chakra-ui/react";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  StyledModalHeader,
  Subtext,
  PasswordHint,
  ToggleButton,
  ResetPasswordButton,
  StyledFormLabel,
} from "../styles";
import { EyeIcon } from "@root/assets/icons/EyeIcon";
import { EyeOffIcon } from "./eyeofficon";
import useSidebar from "@root/hooks/useSidebar";
import { useGlobalContext } from "@root/context/global-context";
import { useSelector } from "react-redux";
import { getLoggedInUser } from "@root/store/selectors/entities";

interface ChangePasswordProps {
  isOpen: boolean;
  onClose: () => void;
}

const ValidationCheck = ({
  isValid,
  text,
}: {
  isValid: boolean;
  text: string;
}) => (
  <Flex align="center" mt={1}>
    <Box as="span" color={isValid ? "green.500" : "red.500"} mr={2}>
      {isValid ? "✔" : "✖"}
    </Box>
    <Text fontSize="sm" color={isValid ? "green.500" : "red.500"}>
      {text}
    </Text>
  </Flex>
);

const ChangePasswordModal = ({ isOpen, onClose }: ChangePasswordProps) => {
  const { t } = useTranslation("common");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [confirmPasswordTouched, setConfirmPasswordTouched] = useState(false);
  const { updateProfile } = useGlobalContext();
  const user: any = useSelector(getLoggedInUser);
  const { resetPasswordLoading } = useGlobalContext();

  // Password validation matching signup: minimum 8 characters and at least 1 number
  const passwordMinLength = (password?.length || 0) >= 8;
  const passwordHasNumber = /\d/.test(password || "");
  const passwordsMatch = password === confirmPassword && password !== "";
  const isPasswordValid = passwordMinLength && passwordHasNumber;
  const allFieldsFilled = password && confirmPassword;
  const canSubmit = isPasswordValid && passwordsMatch && allFieldsFilled;

  // Clear all fields and states when modal closes
  useEffect(() => {
    if (!isOpen) {
      setPassword("");
      setConfirmPassword("");
      setShowPassword(false);
      setShowConfirm(false);
      setPasswordTouched(false);
      setConfirmPasswordTouched(false);
    }
  }, [isOpen]);

  const handleClose = () => {
    setPassword("");
    setConfirmPassword("");
    setShowPassword(false);
    setShowConfirm(false);
    setPasswordTouched(false);
    setConfirmPasswordTouched(false);
    onClose();
  };

  const handleResetPassword = async () => {
    const payload = {
      password,
    };
    const isSuccess = await updateProfile(user?.id, payload);
    if (isSuccess) {
      handleClose();
    }
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleClose} isCentered>
        <ModalOverlay bg="rgba(0, 0, 0, 0.4)" />
        <ModalContent
          maxW="500px"
          bg="#FFFFFF"
          borderRadius="16px"
          p={6}
          boxShadow="lg"
        >
          <StyledModalHeader>
            {t("userProfile.changePassword")}
          </StyledModalHeader>
          <ModalCloseButton />

          <ModalBody>
            <Subtext>{t("userProfile.securePassword")}</Subtext>

            {/* Password Field */}
            <FormControl mb={4}>
              <StyledFormLabel>{t("userProfile.password")}</StyledFormLabel>
              <InputGroup>
                <Input
                  border="1px"
                  borderColor="#D0D5DD"
                  type={showPassword ? "text" : "password"}
                  placeholder={t("userProfile.enterPassword")}
                  autoComplete="off"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onBlur={() => setPasswordTouched(true)}
                />
                <InputRightElement>
                  <ToggleButton onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? (
                      <EyeOffIcon boxSize={4} />
                    ) : (
                      <EyeIcon boxSize={4} />
                    )}
                  </ToggleButton>
                </InputRightElement>
              </InputGroup>
              {/* Password validation UI matching signup */}
              {(passwordTouched || confirmPasswordTouched) && (
                <Box mt={2}>
                  <ValidationCheck
                    isValid={passwordMinLength}
                    text={t("signup.passwordMinLength")}
                  />
                  <ValidationCheck
                    isValid={passwordHasNumber}
                    text={t("signup.passwordMinNumber")}
                  />
                  {password && confirmPassword && (
                    <ValidationCheck
                      isValid={passwordsMatch}
                      text={t("signup.passwordMatch")}
                    />
                  )}
                </Box>
              )}
            </FormControl>

            {/* Confirm Password Field */}
            <FormControl mb={6}>
              <StyledFormLabel>
                {t("userProfile.confirmPassword")}
              </StyledFormLabel>
              <InputGroup>
                <Input
                  border="1px"
                  borderColor="#D0D5DD"
                  type={showConfirm ? "text" : "password"}
                  placeholder={t("userProfile.confirmPassword")}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  onBlur={() => setConfirmPasswordTouched(true)}
                />
                <InputRightElement>
                  <ToggleButton onClick={() => setShowConfirm(!showConfirm)}>
                    {showConfirm ? (
                      <EyeOffIcon boxSize={4} />
                    ) : (
                      <EyeIcon boxSize={4} />
                    )}
                  </ToggleButton>
                </InputRightElement>
              </InputGroup>
            </FormControl>

            <ResetPasswordButton
              onClick={handleResetPassword}
              isDisabled={!canSubmit}
              isLoading={resetPasswordLoading}
            >
              {t("userProfile.resetPassword")}
            </ResetPasswordButton>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ChangePasswordModal;
